import React from 'react';
import {
  FlexRow,
  TaskButton,
  TaskCard,
  getTaskIconUrl,
  useOnMountUnsafe
} from '@make-software/tg-mini-app-ui';
import { Task } from 'make-traffic-integration-core';

import {
  useMakeTrafficManager,
  useCollect,
  useFriends,
  PageWrapper,
  TitleRow, useSeasonInfo
} from '@/shared';
import GoldIcon from '@/shared/assets/icons/gold.svg';
import { EARN_PATH } from '@/app/paths';

import { SectionTitle } from './components/section-title';
import { TaskList } from './components/task-list';

const MAX_RECRUIT_TASKS = 3;
const MAX_VOTING_TASKS = 2;

const splitCategories = (task: Task): string[] =>
  task.category?.split(',').map((c) => c.trim()) ?? [];

const hasCategory = (task: Task, category: string) => splitCategories(task).includes(category);


export const Earn = () => {
  const { tasks, handleSelectTask } = useMakeTrafficManager();
  const { collect } = useCollect();
  const friendsService = useFriends();
  const friendCount = friendsService.data?.friends?.length ?? 0;

  useOnMountUnsafe(async () => {
    await collect({ url: EARN_PATH });
  });

  const { data: seasonInfo } = useSeasonInfo();
  const isSeasonEnded = new Date() > new Date(seasonInfo?.season?.ends_on || '2026-07-26T23:59:59Z');

  const isInAppBrowser = navigator.userAgent.includes('WebView CasperWalletMobile');

  const nonRecruitTasks = React.useMemo(
    () =>
      tasks.filter((t) => !hasCategory(t, 'recruit') && !hasCategory(t, 'vote_project')
      && !(isInAppBrowser && hasCategory(t, 'not_on_cwm'))),
    [tasks]
  );

  const recruitTasks = React.useMemo(
    () =>
      tasks
        .filter((t) => hasCategory(t, 'recruit') && !t.userState.isRewarded)
        .slice(0, MAX_RECRUIT_TASKS),
    [tasks]
  );

  const votingTasks  = React.useMemo(
    () =>
      tasks
        .filter((t) => hasCategory(t, 'vote_project') && !t.userState.isRewarded)
        .slice(0, MAX_VOTING_TASKS),
    [tasks]
  );

  return (
    <PageWrapper padding="0 16px 20px 16px">
      <TitleRow weight={3} color="#FFFFFF" margin="0px 0 8px 0">
        Earn
      </TitleRow>
      <FlexRow gap={8} direction="column">
        {nonRecruitTasks.map((t) => {
          return (
            <TaskCard
              key={t.id}
              task={t}
              iconUrl={getTaskIconUrl(t)}
              rewardLabel="Earn:"
              borderRadius="8px"
              border="1px solid #41424e"
              background="#30313b"
              titleFont={{ fontFamily: 'Inter' }}
              rewardLabelFont={{
                fontFamily: 'Inter',
                fontWeight: '400',
                color: '#a9aaad !important'
              }}
              rewardsProps={{
                earnIconSrc: GoldIcon,
                earnIconColor: 'auto',
                rewardIcons: {
                  points: window.config.assets_path + '/gold.svg',
                  plays: window.config.assets_path + '/fantom-run.svg'
                },
                iconSize: 15,
                valueFont: {
                  fontFamily: 'JetBrains Mono',
                  fontWeight: 700,
                  fontSize: '14px'
                }
              }}
              actionButton={(task) => (
                <TaskButton
                  task={task}
                  disabled={isSeasonEnded}
                  friendCount={friendCount}
                  goButtonVariant="outlined"
                  width={48}
                  onClick={() => handleSelectTask(task)}
                  doneButton={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 400 }}
                  goButton={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 400 }}
                />
              )}
            />
          );
        })}
      </FlexRow>

      {votingTasks?.length > 0 && (
        <>
          <SectionTitle>Vote your favorite projects</SectionTitle>
          <TaskList tasks={votingTasks} friendCount={friendCount} onTaskClick={handleSelectTask} bottomMargin="26px" />
        </>
      )}

      {recruitTasks?.length > 0 && (
        <>
          <SectionTitle>Recruit friends</SectionTitle>
          <TaskList tasks={recruitTasks} friendCount={friendCount} onTaskClick={handleSelectTask} bottomMargin="26px" />
        </>
      )}
    </PageWrapper>
  );
};
